import React from 'react';
import { Box, ButtonBase, Typography, useTheme } from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import TableDropdownMenu from '../../global/TableDropdownMenu';
import { getTextForCategory } from '../../../helpers/items';
import { formatDollarAmount } from '../../../utils/global/helper';
import {
  ProductInView,
  priceRangeLabel,
} from '../../../utils/catalog/catalogFilters';
import ItemThumbnail from './ItemThumbnail';
import { HIDE_ON_PHONE, THUMB_GAP, libraryRowSx } from './layout';

interface Props {
  entry: ProductInView;
  isOpen: boolean;
  busy: boolean;
  actions: React.ComponentProps<typeof TableDropdownMenu>['options'];
  onToggle: (productId: string) => void;
}

export default function LibraryProductRow({
  entry,
  isOpen,
  busy,
  actions,
  onToggle,
}: Props) {
  const theme = useTheme();
  const { product, variants } = entry;
  const typeLabel = product.typeValues.map(t => t.label).join(', ');
  const details = [
    product.brand?.name,
    typeLabel,
    `${variants.length} variant${variants.length === 1 ? '' : 's'}`,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <ButtonBase
      component="div"
      aria-expanded={isOpen}
      onClick={() => onToggle(product.id)}
      sx={{
        ...libraryRowSx,
        width: '100%',
        textAlign: 'left',
        fontFamily: theme.typography.fontFamily,
        bgcolor: isOpen ? theme.palette.lightGrey.main : 'transparent',
        '&:hover': { bgcolor: theme.palette.accent.main },
      }}>
      <ExpandMoreRoundedIcon
        sx={{
          color: 'text.secondary',
          transform: isOpen ? 'none' : 'rotate(-90deg)',
          transition: 'transform 0.2s ease',
        }}
      />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: THUMB_GAP,
          minWidth: 0,
        }}>
        <ItemThumbnail url={product.coverImageUrl} name={product.name} />
        <Box minWidth={0}>
          <Typography variant="subtitle2" noWrap>
            {product.name}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            display="block">
            {details}
          </Typography>
        </Box>
      </Box>
      <Typography
        variant="body2"
        color="text.secondary"
        noWrap
        sx={{ display: HIDE_ON_PHONE }}>
        {getTextForCategory(product.category)}
      </Typography>
      <Typography
        variant="body2"
        textAlign="right"
        sx={{ whiteSpace: { xs: 'normal', sm: 'nowrap' } }}>
        {priceRangeLabel(variants, amount => formatDollarAmount(amount))}
      </Typography>
      <Box
        component="span"
        onClick={e => e.stopPropagation()}
        onKeyDown={e => e.stopPropagation()}>
        <TableDropdownMenu disabled={busy} options={actions} />
      </Box>
    </ButtonBase>
  );
}
